import { useState } from 'react';
import { useFieldArray, useFormContext, useController } from 'react-hook-form';
import { useIntl } from 'react-intl';
import DateSpanRow from './DateSpanRow';
import WeekdayChips from './WeekdayChips';
import AddSpanButton from './AddSpanButton';
import StepHeading from './StepHeading';
import { ConfigurationFormData } from './formData';

interface Props {
  disabled?: boolean;
}

export default function DateWindowsField({ disabled = false }: Props) {
  const intl = useIntl();
  const { control, register, formState } = useFormContext<ConfigurationFormData>();
  const { fields, append, remove } = useFieldArray({ control, name: 'windows' });
  const [addedIndex, setAddedIndex] = useState<number | null>(null);
  const [announcement, setAnnouncement] = useState('');

  const addWindow = () => {
    append({ from: '', to: '', weekdays: [] });
    setAddedIndex(fields.length);
    setAnnouncement(intl.formatMessage({ id: 'accounts.configForm.windowAdded' }));
  };

  const removeWindow = (index: number) => {
    remove(index);
    setAddedIndex(null);
    setAnnouncement(intl.formatMessage({ id: 'accounts.configForm.windowRemoved' }));
  };

  const required = intl.formatMessage({ id: 'accounts.configForm.dateRequired' });

  return (
    <fieldset>
      <StepHeading labelId="accounts.configForm.windowsHeading" />

      <div className="mt-2 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="divide-y divide-slate-100">
          {fields.map((field, index) => (
            <DateSpanRow
              key={field.id}
              idPrefix={`window-${index}`}
              from={register(`windows.${index}.from`, { required })}
              to={register(`windows.${index}.to`, {
                required,
                validate: (value, values) =>
                  value >= values.windows[index].from ||
                  intl.formatMessage({ id: 'accounts.configForm.maxDateError' }),
              })}
              fromError={formState.errors.windows?.[index]?.from}
              toError={formState.errors.windows?.[index]?.to}
              autoFocus={index === addedIndex}
              disabled={disabled}
              removeLabel={
                fields.length > 1
                  ? intl.formatMessage({ id: 'accounts.configForm.removeWindow' })
                  : undefined
              }
              onRemove={fields.length > 1 ? () => removeWindow(index) : undefined}
            >
              <WeekdaysControl index={index} disabled={disabled} />
            </DateSpanRow>
          ))}
        </div>

        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-2">
          <AddSpanButton
            onClick={addWindow}
            disabled={disabled}
            labelId="accounts.configForm.addWindow"
          />
        </div>
      </div>

      <p role="status" aria-live="polite" className="sr-only">
        {announcement}
      </p>
    </fieldset>
  );
}

function WeekdaysControl({ index, disabled }: { index: number; disabled: boolean }) {
  const { control } = useFormContext<ConfigurationFormData>();
  const { field } = useController({ control, name: `windows.${index}.weekdays` });

  return <WeekdayChips value={field.value} onChange={field.onChange} disabled={disabled} />;
}
